import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { C1ChartCard } from './C1ChartCard';

export interface C1DonutSlice {
  key: string;
  label: string;
  value: number;
  /** Overrides the ordered ramp. Pass one for every slice or none at all. */
  color?: string;
}

interface C1DonutProps {
  title: string;
  slices: C1DonutSlice[];
  /** Formats every figure the reader sees: legend, table and the centre. */
  format?: (value: number) => string;
  /**
   * The figure in the hole. Defaults to the total; pass an explicit pair to show
   * something else, or `null` to leave the hole empty.
   */
  centre?: { value: ReactNode; label?: ReactNode } | null;
  caption?: ReactNode;
  emptyMessage?: ReactNode;
  /** Stretches the card across a two-column chart grid. */
  wide?: boolean;
}

/**
 * An ordered single-hue ramp, dark to light.
 *
 * NOT SIX ARBITRARY HUES. A donut's slices are parts of one total, and on the
 * ordered scales this is actually asked for — a grade band, a collection status, a
 * risk tier — a sequence of one hue says "these are steps of the same thing", which
 * six unrelated colours would deny. It also sidesteps inventing a categorical
 * palette that nobody has checked for colour-vision separation.
 *
 * The light end of the ramp is deliberately not load-bearing: the ring is never the
 * only channel. Every slice is named and numbered in the legend and repeated in the
 * table behind the toggle, so a reader who cannot separate two adjacent tints still
 * gets every value. Categorical data with no natural order should pass explicit
 * `color`s instead of leaning on this.
 */
const RAMP = ['#0b3aa0', '#1a56db', '#5b86e8', '#9db8f2', '#c7d7f8', '#e3ebfc'] as const;

const RADIUS = 60;
const STROKE = 26;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
/** Lets the surface through between slices, so a boundary survives similar tints. */
const GAP = 1.5;

/**
 * A part-to-whole ring.
 *
 * WHY A DONUT IS ALLOWED HERE when a pie generally is not: the hole carries the
 * total, so the reader gets the one figure a pie makes them reconstruct, and the
 * legend carries each part as a number rather than asking anyone to judge an angle.
 * Use it for a handful of parts of one meaningful total. For comparing magnitudes
 * across categories that do not sum to anything, a bar is the honest form.
 */
export function C1Donut({
  title,
  slices,
  format = (value) => String(value),
  centre,
  caption,
  emptyMessage,
  wide,
}: C1DonutProps) {
  const { t } = useTranslation();
  const [showTable, setShowTable] = useState(false);

  const drawable = slices.filter((slice) => slice.value > 0);
  const total = drawable.reduce((sum, slice) => sum + slice.value, 0);

  if (total <= 0) {
    return (
      <C1ChartCard title={title} wide={wide}>
        <p className="c1-chartEmpty">{emptyMessage ?? t('c1.noData')}</p>
      </C1ChartCard>
    );
  }

  const share = (value: number) => Math.round((value / total) * 100);
  const colorOf = (slice: C1DonutSlice, index: number) =>
    slice.color ?? RAMP[index % RAMP.length];

  let consumed = 0;
  const arcs = drawable.map((slice, index) => {
    const length = (slice.value / total) * CIRCUMFERENCE;
    const arc = {
      key: slice.key,
      color: colorOf(slice, index),
      // A single slice filling the ring has no neighbour to be separated from, and
      // shortening it would open a notch in what is really an unbroken circle.
      dash: drawable.length === 1 ? length : Math.max(length - GAP, 0.5),
      offset: -consumed,
    };
    consumed += length;
    return arc;
  });

  const description = drawable
    .map((slice) => `${slice.label} ${format(slice.value)} (${share(slice.value)}%)`)
    .join(', ');

  return (
    <C1ChartCard
      title={title}
      caption={caption}
      wide={wide}
      action={
        <button
          type="button"
          className="c1-tableToggle"
          onClick={() => setShowTable((on) => !on)}
          aria-pressed={showTable}
        >
          {showTable ? t('c1.showChart') : t('c1.showTable')}
        </button>
      }
    >
      {showTable ? (
        <table className="c1-table">
          <caption className="sr-only">{title}</caption>
          <thead>
            <tr>
              <th scope="col">{t('c1.category')}</th>
              <th scope="col" className="c1-cell--end">
                {t('c1.value')}
              </th>
              <th scope="col" className="c1-cell--end">
                {t('c1.share')}
              </th>
            </tr>
          </thead>
          <tbody>
            {slices.map((slice) => (
              <tr key={slice.key}>
                <th scope="row">{slice.label}</th>
                <td className="c1-cell--end">{format(slice.value)}</td>
                <td className="c1-cell--end">{share(slice.value)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="c1-donut">
          <div className="c1-donutRing">
            {/* One label for the whole ring. Reading out twelve <circle> elements
                tells a screen reader nothing; the sentence carries every value, and
                the table behind the toggle carries them in a navigable form. */}
            <svg viewBox="0 0 160 160" role="img" aria-label={`${title}: ${description}`}>
              <g transform="rotate(-90 80 80)">
                {arcs.map((arc) => (
                  <circle
                    key={arc.key}
                    cx="80"
                    cy="80"
                    r={RADIUS}
                    fill="none"
                    stroke={arc.color}
                    strokeWidth={STROKE}
                    strokeDasharray={`${arc.dash} ${CIRCUMFERENCE - arc.dash}`}
                    strokeDashoffset={arc.offset}
                  />
                ))}
              </g>
            </svg>
            {centre !== null && (
              <div className="c1-donutCentre" aria-hidden="true">
                <span className="c1-donutCentreValue">{centre?.value ?? format(total)}</span>
                {centre?.label ? (
                  <span className="c1-donutCentreLabel">{centre.label}</span>
                ) : null}
              </div>
            )}
          </div>
          <ul className="c1-donutLegend">
            {drawable.map((slice, index) => (
              <li key={slice.key} className="c1-donutLegendItem">
                <span
                  className="c1-donutSwatch"
                  style={{ background: colorOf(slice, index) }}
                  aria-hidden="true"
                />
                <span className="c1-donutLegendLabel">{slice.label}</span>
                <span className="c1-donutLegendValue">{share(slice.value)}%</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </C1ChartCard>
  );
}
